import { isAppDebugEnabled, logExpandedDebug } from '@/utils/debug/expandedConsoleLog';
import { analyzeDefectGeoMatch, buildModuleCatalogFromOlFeatures } from '@/utils/debug/defectModuleGeoVerify';
import { resolveDefectHierarchyFields } from '@/features/defect-drawer/utils/resolveDefectHierarchyFields';
import { defectBelongsToModule } from '@/features/defect-drawer/utils/defectModuleMatch';
import {
  buildModuleTreeFromSiteDesign,
  filterZeroDefectModules
} from '@/features/defect-drawer/utils/defectGrouping';
import {
  filterDefectsByAiVisibility,
  filterDefectsByToolbarTypes,
  isAiOnlyWfsDefectRow,
  WFS_DEFECT_SOURCE
} from '@/features/defect-drawer/utils/defectFilter';
import {
  findModuleInDefectTree,
  findModuleInSiteDesignHierarchy,
  summarizeDefectHierarchySample,
  summarizeHierarchyTree
} from '@/features/defect-drawer/utils/moduleListSyncDebug';
import { resolveSolarModuleWfsProperties } from '@/utils/map/resolveSolarModuleWfsProperties';
import { resolveWfsSolarModuleSeq } from '@/utils/defect/defectShapeOverlay';

export const DEFECT_MODULE_LIST_DIAG_PREFIX = '[DEFECT-MODULE-LIST-DIAG]';

let instructionsPrinted = false;

/**
 * @param {string} event
 * @param {Record<string, unknown>} [detail]
 */
export function logDefectModuleListDiag(event, detail = {}) {
  logExpandedDebug(DEFECT_MODULE_LIST_DIAG_PREFIX, event, detail);
}

/**
 * DF-ID-00049579 | 49579 | "49579" → 49579
 * @param {number|string} input
 * @returns {number|null}
 */
export function parseDefectSeqInput(input) {
  if (input == null || input === '') {
    return null;
  }
  const raw = String(input).trim();
  const dfMatch = raw.match(/DF-ID-0*(\d+)/i);
  if (dfMatch) {
    const n = Number(dfMatch[1]);
    return Number.isFinite(n) && n > 0 ? n : null;
  }
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * @param {object} defect
 * @param {object[]} toolbarDefectFilters
 * @param {(row: object) => number|null} resolveDefectTypeRowId
 */
export function explainToolbarTypeFilter(defect, toolbarDefectFilters, resolveDefectTypeRowId) {
  const filters = Array.isArray(toolbarDefectFilters) ? toolbarDefectFilters : [];
  if (!filters.length) {
    return { passes: true, reason: 'toolbar_defect_filters_empty' };
  }
  if (typeof resolveDefectTypeRowId !== 'function') {
    return { passes: true, reason: 'resolveDefectTypeRowId_missing_skipped' };
  }

  const allowedNumericIds = new Set();
  const allowedCodes = new Set();
  filters.forEach(dt => {
    const id = resolveDefectTypeRowId(dt);
    if (id != null) {
      allowedNumericIds.add(String(id));
    }
    if (dt?.code) {
      allowedCodes.add(String(dt.code).toLowerCase());
    }
  });

  if (defect?.defectCode != null && allowedNumericIds.has(String(defect.defectCode))) {
    return { passes: true, reason: 'defectCode matches toolbar filter id' };
  }

  const typeStr = String(defect?.defectType ?? '').toLowerCase();
  if (!typeStr) {
    return {
      passes: false,
      reason: 'defectType empty — toolbar has active filters',
      allowedCodes: [...allowedCodes],
      allowedNumericIds: [...allowedNumericIds],
      hint: 'AI 미확정 결함(defect_type null/empty)은 유형 필터 ON 시 모듈리스트에서 제외됨'
    };
  }

  if (allowedCodes.has(typeStr)) {
    return { passes: true, reason: 'defectType code matches toolbar filter' };
  }
  if (allowedNumericIds.has(typeStr)) {
    return { passes: true, reason: 'defectType id matches toolbar filter' };
  }

  return {
    passes: false,
    reason:
      typeStr === 'ai'
        ? 'defectType is WFS placeholder "ai" — not a parent type (CELL, Hotspot, …)'
        : `defectType "${typeStr}" not in toolbar selection`,
    defectType: typeStr,
    allowedCodes: [...allowedCodes],
    allowedNumericIds: [...allowedNumericIds],
    hint:
      typeStr === 'ai'
        ? '지도 툴바 결함 유형 필터를 전부 해제하거나, 결함 유형을 확정(Cell 등) 저장하세요'
        : '지도 툴바 결함 유형 필터를 확인하세요 (AI 토글과 별개)'
  };
}

/**
 * WFS 피처 properties → store 행과 동일한 형태 요약
 * @param {object} wfsProps
 */
export function summarizeWfsFeatureProperties(wfsProps) {
  if (!wfsProps || typeof wfsProps !== 'object') {
    return null;
  }
  const moduleProps = resolveSolarModuleWfsProperties(wfsProps);
  return {
    seq: wfsProps.seq ?? wfsProps.id ?? null,
    solar_module_seq: wfsProps.solar_module_seq ?? wfsProps.solarModuleSeq ?? null,
    resolvedSolarModuleSeq: resolveWfsSolarModuleSeq(wfsProps),
    module_id: wfsProps.module_id ?? wfsProps.moduleId ?? moduleProps.moduleId ?? null,
    string_id: wfsProps.string_id ?? wfsProps.stringId ?? moduleProps.stringId ?? null,
    array_id: wfsProps.array_id ?? wfsProps.arrayId ?? moduleProps.arrayId ?? null,
    solar_string_seq: wfsProps.solar_string_seq ?? wfsProps.solarStringSeq ?? null,
    solar_array_seq: wfsProps.solar_array_seq ?? wfsProps.solarArraySeq ?? null,
    detect_way: wfsProps.detect_way ?? wfsProps.defect_way ?? null,
    defect_type: wfsProps.defect_type ?? null,
    defect_code: wfsProps.defect_code ?? null,
    defect_status: wfsProps.defect_status ?? null,
    thermal_img_seq: wfsProps.thermal_img_seq ?? null,
    img_name: wfsProps.img_name ?? null,
    ml_defect_confirmed: wfsProps.ml_defect_confirmed ?? null
  };
}

/**
 * @param {object} storeRow
 */
export function summarizeStoreWfsRow(storeRow) {
  if (!storeRow) {
    return null;
  }
  const hierarchy = resolveDefectHierarchyFields(storeRow);
  return {
    seq: storeRow.seq ?? null,
    wfsSource: storeRow.wfsSource ?? null,
    solarModuleSeq: storeRow.solarModuleSeq ?? null,
    moduleId: storeRow.moduleId ?? storeRow.module_id ?? null,
    stringId: storeRow.stringId ?? storeRow.string_id ?? null,
    arrayId: storeRow.arrayId ?? storeRow.array_id ?? null,
    solarStringSeq: storeRow.solarStringSeq ?? storeRow.solar_string_seq ?? null,
    solarArraySeq: storeRow.solarArraySeq ?? storeRow.solar_array_seq ?? null,
    detectWay: storeRow.detectWay ?? null,
    defectType: storeRow.defectType ?? null,
    defectCode: storeRow.defectCode ?? null,
    lng: storeRow.lng ?? null,
    lat: storeRow.lat ?? null,
    imgName: storeRow.imgName ?? null,
    thermalImgSeq: storeRow.thermalImgSeq ?? null,
    resolvedHierarchy: hierarchy,
    isAiOnlyRow: isAiOnlyWfsDefectRow(storeRow)
  };
}

/**
 * siteDesign 트리에서 결함이 붙을 모듈 노드 탐색 (시뮬레이션)
 * @param {object[]} hierarchy
 * @param {object[]} filteredDefects
 * @param {number|string} solarModuleSeq
 */
export function findDefectModulePlacementInTree(hierarchy, filteredDefects, solarModuleSeq) {
  if (!hierarchy?.length || solarModuleSeq == null) {
    return { found: false, reason: 'no_hierarchy_or_module_seq' };
  }

  const tree = filterZeroDefectModules(
    buildModuleTreeFromSiteDesign({
      hierarchy,
      defects: filteredDefects || [],
      fastDefectLabel: d => String(d?.defectType ?? d?.defectCode ?? ''),
      defectLabelFallback: 'defect'
    })
  );

  const inTree = findModuleInDefectTree(tree, solarModuleSeq);
  if (inTree) {
    return {
      found: true,
      placement: 'siteDesign_tree',
      moduleSeq: inTree.moduleSeq,
      moduleDisplayId: inTree.moduleDisplayId,
      defectCount: inTree.defectCount,
      treeArrayCount: tree.length
    };
  }

  return {
    found: false,
    placement: 'not_in_filtered_tree',
    reason: 'module may have zero defects after filters, or seq mismatch',
    treeArrayCount: tree.length
  };
}

/**
 * @param {object} ctx
 * @param {import('vuex').Store} [ctx.store]
 * @param {number|string} ctx.defectSeq
 * @param {object} [ctx.wfsFeatureProps] — OL feature properties (팝업)
 * @param {import('ol/Feature').default[]} [ctx.moduleFeatures]
 * @param {object[]} [ctx.parentDefectTypes]
 * @param {(row: object) => number|null} [ctx.resolveDefectTypeRowId]
 * @param {string} [ctx.trigger]
 */
export function diagnoseDefectModuleListMapping(ctx = {}) {
  const defectSeq = parseDefectSeqInput(ctx.defectSeq);
  const store = ctx.store;
  const dashboard = store?.state?.dashboard ?? {};
  const wfsRows = dashboard.wfsDefectRows || [];
  const storeRow = wfsRows.find(d => Number(d?.seq) === Number(defectSeq)) || null;
  const hierarchy = dashboard.siteDesignModuleHierarchy || [];
  const showAiDefectFlag = dashboard.showAiDefectFlag !== false;
  const toolbarDefectFilters = dashboard.toolbarDefectFilters || [];
  const moduleListOpen = dashboard.defectDrawerOpen === true;
  const resolveTypeId = ctx.resolveDefectTypeRowId;

  const wfsFromPopup = ctx.wfsFeatureProps
    ? summarizeWfsFeatureProperties(ctx.wfsFeatureProps)
    : null;

  const steps = [];

  // ── Step 1: store row ──
  if (!storeRow) {
    steps.push({
      step: '1_store_wfs_row',
      ok: false,
      detail: {
        defectSeq,
        wfsDefectRowsCount: wfsRows.length,
        wfsDefectRowsReportId: dashboard.wfsDefectRowsReportId ?? null,
        hint: 'AI/확정 WFS 로드 전이거나 seq 불일치 — 모듈리스트 ON + 결함 마커 ON 후 재시도'
      }
    });
  } else {
    steps.push({
      step: '1_store_wfs_row',
      ok: true,
      detail: summarizeStoreWfsRow(storeRow)
    });
  }

  const effectiveRow = storeRow || (wfsFromPopup ? { ...wfsFromPopup, seq: defectSeq } : null);
  const hierarchyFields = effectiveRow ? resolveDefectHierarchyFields(effectiveRow) : null;
  const solarModuleSeq =
    hierarchyFields?.solarModuleSeq ??
    wfsFromPopup?.resolvedSolarModuleSeq ??
    null;

  // ── Step 2: WFS popup vs store ──
  if (wfsFromPopup) {
    const storeSeq = storeRow?.solarModuleSeq ?? null;
    const popupSeq = wfsFromPopup.resolvedSolarModuleSeq ?? null;
    steps.push({
      step: '2_popup_wfs_vs_store',
      ok: storeRow != null && String(storeSeq ?? '') === String(popupSeq ?? ''),
      detail: {
        popupWfs: wfsFromPopup,
        storeSolarModuleSeq: storeSeq,
        popupSolarModuleSeq: popupSeq,
        mismatch:
          storeRow && String(storeSeq ?? '') !== String(popupSeq ?? '')
            ? 'store row differs from clicked WFS feature'
            : null
      }
    });
  }

  // ── Step 3: DB FK (solarModuleSeq) ──
  steps.push({
    step: '3_db_module_fk',
    ok: solarModuleSeq != null && solarModuleSeq !== '',
    detail: {
      solarModuleSeq,
      wfsDisplayIds: {
        arrayId: hierarchyFields?.arrayDisplayId ?? wfsFromPopup?.array_id ?? null,
        stringId: hierarchyFields?.stringDisplayId ?? wfsFromPopup?.string_id ?? null,
        moduleId: hierarchyFields?.moduleDisplayId ?? wfsFromPopup?.module_id ?? null
      },
      hint:
        solarModuleSeq == null
          ? 'WFS solar_module_seq null → DB orphan (findContainingPoint 실패)'
          : 'WFS/DB FK 존재 — 모듈리스트는 추가 조건 필요'
    }
  });

  // ── Step 4: AI visibility ──
  const aiFiltered = storeRow
    ? filterDefectsByAiVisibility([storeRow], showAiDefectFlag)
    : [];
  const passesAi = storeRow ? aiFiltered.length > 0 : null;
  steps.push({
    step: '4_ai_visibility_filter',
    ok: passesAi !== false,
    detail: {
      showAiDefectFlag,
      wfsSource: storeRow?.wfsSource ?? null,
      isAiOnlyRow: storeRow ? isAiOnlyWfsDefectRow(storeRow) : null,
      passes: passesAi,
      hint:
        passesAi === false
          ? 'showAiDefectFlag OFF + wfsSource ai-only → 모듈리스트 제외'
          : 'AI 토글 ON이면 ai-only 행도 통과'
    }
  });

  // ── Step 5: Toolbar type filter ──
  const toolbarExplain = storeRow
    ? explainToolbarTypeFilter(storeRow, toolbarDefectFilters, resolveTypeId)
    : { passes: null, reason: 'no_store_row' };
  steps.push({
    step: '5_toolbar_defect_type_filter',
    ok: toolbarExplain.passes !== false,
    detail: {
      activeFilterCount: toolbarDefectFilters.length,
      activeFilters: toolbarDefectFilters.map(f => ({
        id: resolveTypeId?.(f) ?? f?.id ?? null,
        code: f?.code ?? null,
        name: f?.name ?? null
      })),
      ...toolbarExplain
    }
  });

  const filteredDefects = storeRow
    ? filterDefectsByToolbarTypes(
        filterDefectsByAiVisibility(wfsRows, showAiDefectFlag),
        toolbarDefectFilters,
        { resolveDefectTypeRowId: resolveTypeId }
      )
    : [];
  const inFilteredList = storeRow
    ? filteredDefects.some(d => Number(d.seq) === Number(defectSeq))
    : false;

  steps.push({
    step: '5b_in_filteredDefects',
    ok: inFilteredList,
    detail: {
      inFilteredList,
      filteredDefectCount: filteredDefects.length,
      totalWfsRows: wfsRows.length,
      hint: !inFilteredList
        ? 'filteredDefects에 없음 → defectTree count=0 → filterZeroDefectModules로 Module 행 숨김'
        : 'filteredDefects 포함 — 트리 빌드 대상'
    }
  });

  // ── Step 6: siteDesign hierarchy ──
  const siteDesignMatch =
    solarModuleSeq != null ? findModuleInSiteDesignHierarchy(hierarchy, solarModuleSeq) : null;
  steps.push({
    step: '6_siteDesign_hierarchy',
    ok: !!siteDesignMatch,
    detail: {
      moduleListOpen,
      hierarchyLoaded: hierarchy.length > 0,
      hierarchySummary: summarizeHierarchyTree(hierarchy),
      solarModuleSeq,
      siteDesignMatch,
      hint: !siteDesignMatch && solarModuleSeq != null
        ? `PK ${solarModuleSeq}이 siteDesign 트리에 없음 → orphan 버킷(맨 위 Array ?)으로 이동`
        : siteDesignMatch
          ? `본 트리: Array ${siteDesignMatch.arrayDisplayId} · String ${siteDesignMatch.stringDisplayId} · Module ${siteDesignMatch.moduleDisplayId}`
          : 'siteDesign 미로드 — 모듈리스트 ON + siteDesign 로드 필요'
    }
  });

  // ── Step 7: displayId cross-check ──
  if (siteDesignMatch && hierarchyFields) {
    const displayMismatch = [];
    if (
      hierarchyFields.moduleDisplayId != null &&
      siteDesignMatch.moduleDisplayId != null &&
      String(hierarchyFields.moduleDisplayId) !== String(siteDesignMatch.moduleDisplayId)
    ) {
      displayMismatch.push('moduleDisplayId');
    }
    if (
      hierarchyFields.stringDisplayId != null &&
      siteDesignMatch.stringDisplayId != null &&
      String(hierarchyFields.stringDisplayId) !== String(siteDesignMatch.stringDisplayId)
    ) {
      displayMismatch.push('stringDisplayId');
    }
    if (
      hierarchyFields.arrayDisplayId != null &&
      siteDesignMatch.arrayDisplayId != null &&
      String(hierarchyFields.arrayDisplayId) !== String(siteDesignMatch.arrayDisplayId)
    ) {
      displayMismatch.push('arrayDisplayId');
    }
    steps.push({
      step: '7_wfs_display_id_vs_siteDesign',
      ok: displayMismatch.length === 0,
      detail: {
        wfs: {
          arrayId: hierarchyFields.arrayDisplayId,
          stringId: hierarchyFields.stringDisplayId,
          moduleId: hierarchyFields.moduleDisplayId
        },
        siteDesign: {
          arrayDisplayId: siteDesignMatch.arrayDisplayId,
          stringDisplayId: siteDesignMatch.stringDisplayId,
          moduleDisplayId: siteDesignMatch.moduleDisplayId
        },
        displayMismatch: displayMismatch.length ? displayMismatch : null
      }
    });
  }

  // ── Step 8: tree placement simulation ──
  const placement = findDefectModulePlacementInTree(
    hierarchy,
    filteredDefects,
    solarModuleSeq
  );
  steps.push({
    step: '8_module_list_tree_placement',
    ok: placement.found,
    detail: {
      ...placement,
      orphanLikely:
        solarModuleSeq != null && inFilteredList && !siteDesignMatch,
      hint: placement.found
        ? `모듈리스트에 표시되어야 함 — Array/String 펼침 확인`
        : !inFilteredList
          ? '필터에서 제외됨 (step 4·5)'
          : !siteDesignMatch
            ? 'orphan 섹션(맨 위) 확인 — 본 트리 Module 행은 count=0으로 숨겨짐'
            : 'siteDesign seq 불일치 또는 트리 미빌드'
    }
  });

  // ── Step 9: geo (optional) ──
  if (ctx.moduleFeatures?.length && effectiveRow) {
    const catalog = buildModuleCatalogFromOlFeatures(ctx.moduleFeatures);
    const geoRow = {
      seq: defectSeq,
      lng: storeRow?.lng ?? null,
      lat: storeRow?.lat ?? null,
      solarModuleSeq
    };
    const geo = analyzeDefectGeoMatch(geoRow, catalog);
    steps.push({
      step: '9_geo_point_vs_module_polygons',
      ok: geo.verdict === 'linked_and_inside' || geo.clientInsideModule,
      detail: geo
    });
  }

  // ── Verdict ──
  let verdict = 'unknown';
  let summary = '';

  if (!storeRow) {
    verdict = 'not_in_wfsDefectRows';
    summary = 'store wfsDefectRows에 결함 없음';
  } else if (solarModuleSeq == null) {
    verdict = 'db_orphan_no_solar_module_seq';
    summary = 'DB solar_module FK 없음 — WFS array/module_id는 있어도 모듈리스트 orphan';
  } else if (!inFilteredList) {
    verdict = passesAi === false ? 'filtered_by_ai_toggle' : 'filtered_by_toolbar_type';
    summary =
      passesAi === false
        ? 'AI 표시 OFF로 ai-only 행 제외'
        : '지도 결함 유형 필터에 걸려 모듈리스트 제외';
  } else if (!siteDesignMatch) {
    verdict = 'not_in_siteDesign_hierarchy';
    summary = `solarModuleSeq ${solarModuleSeq}은 siteDesign에 없음 → orphan 버킷(맨 위)`;
  } else if (!placement.found) {
    verdict = 'in_hierarchy_but_tree_empty';
    summary = 'siteDesign에는 있으나 트리 Module 행 없음 — filterZeroDefectModules';
  } else {
    verdict = 'should_appear_under_module';
    summary = `정상: Array ${siteDesignMatch.arrayDisplayId} · String ${siteDesignMatch.stringDisplayId} · Module ${siteDesignMatch.moduleDisplayId} — 펼침 확인`;
  }

  const report = {
    trigger: ctx.trigger ?? 'manual',
    defectSeq,
    dfId: defectSeq != null ? `DF-ID-${String(defectSeq).padStart(8, '0')}` : null,
    moduleListOpen,
    verdict,
    summary,
    steps,
    quickCheck: {
      storeRow: summarizeStoreWfsRow(storeRow),
      popupWfs: wfsFromPopup,
      inFilteredDefects: inFilteredList,
      siteDesignMatch,
      treePlacement: placement
    },
    howToUse: {
      console: '__solvitDefectModuleListDiag.diagnose(49579)',
      filter: DEFECT_MODULE_LIST_DIAG_PREFIX
    }
  };

  return report;
}

/**
 * @param {object} report
 */
export function printDefectModuleListMappingReport(report) {
  if (!instructionsPrinted) {
    instructionsPrinted = true;
    logDefectModuleListDiag('USER-STEPS', {
      filter: DEFECT_MODULE_LIST_DIAG_PREFIX,
      step1: '모듈리스트 ON + 결함 마커(AI) ON',
      step2: '결함 클릭 → 팝업에서 자동 진단 또는',
      step3: '__solvitDefectModuleListDiag.diagnose("DF-ID-00049579")',
      step4: '__solvitDefectModuleListDiag.diagnose(49579)',
      step5: 'verdict / summary / steps[] 순서대로 원인 확인'
    });
  }

  logDefectModuleListDiag('report', {
    dfId: report.dfId,
    defectSeq: report.defectSeq,
    verdict: report.verdict,
    summary: report.summary,
    moduleListOpen: report.moduleListOpen
  });

  for (const step of report.steps || []) {
    const label = step.ok === false ? 'FAIL' : step.ok === true ? 'OK' : 'INFO';
    logDefectModuleListDiag(`step.${step.step} [${label}]`, step.detail);
  }

  logDefectModuleListDiag('quickCheck', report.quickCheck);

  console.warn(
    `${DEFECT_MODULE_LIST_DIAG_PREFIX} summary`,
    report.dfId ?? report.defectSeq,
    `verdict=${report.verdict}`,
    `→ ${report.summary}`
  );

  return report;
}

let getDiagContext = null;

/**
 * @param {() => object} contextFn — { store, moduleFeatures?, parentDefectTypes?, resolveDefectTypeRowId? }
 */
export function installDefectModuleListMappingDebugBridge(contextFn) {
  if (!isAppDebugEnabled()) {
    return;
  }
  getDiagContext = contextFn;

  if (typeof window === 'undefined') {
    return;
  }

  window.__solvitDefectModuleListDiag = {
    printInstructions: () => {
      instructionsPrinted = false;
      printDefectModuleListMappingReport({
        defectSeq: null,
        verdict: 'help',
        summary: 'see USER-STEPS',
        steps: [],
        quickCheck: {}
      });
    },
    diagnose: (defectSeqOrDfId, options = {}) => {
      const ctx = getDiagContext?.() ?? {};
      const report = diagnoseDefectModuleListMapping({
        store: ctx.store,
        defectSeq: defectSeqOrDfId,
        moduleFeatures: ctx.moduleFeatures,
        parentDefectTypes: ctx.parentDefectTypes,
        resolveDefectTypeRowId: ctx.resolveDefectTypeRowId,
        wfsFeatureProps: options.wfsFeatureProps ?? null,
        trigger: options.trigger ?? 'manual-diagnose'
      });
      return printDefectModuleListMappingReport(report);
    },
    diagnoseFromWfsProps: (wfsFeatureProps, options = {}) => {
      const seq =
        parseDefectSeqInput(wfsFeatureProps?.seq) ??
        parseDefectSeqInput(wfsFeatureProps?.id);
      const ctx = getDiagContext?.() ?? {};
      const report = diagnoseDefectModuleListMapping({
        store: ctx.store,
        defectSeq: seq,
        moduleFeatures: ctx.moduleFeatures,
        parentDefectTypes: ctx.parentDefectTypes,
        resolveDefectTypeRowId: ctx.resolveDefectTypeRowId,
        wfsFeatureProps,
        trigger: options.trigger ?? 'wfs-props'
      });
      return printDefectModuleListMappingReport(report);
    },
    lastReport: null
  };

  const origDiagnose = window.__solvitDefectModuleListDiag.diagnose;
  window.__solvitDefectModuleListDiag.diagnose = (...args) => {
    const report = origDiagnose(...args);
    window.__solvitDefectModuleListDiag.lastReport = report;
    return report;
  };
  const origFromProps = window.__solvitDefectModuleListDiag.diagnoseFromWfsProps;
  window.__solvitDefectModuleListDiag.diagnoseFromWfsProps = (...args) => {
    const report = origFromProps(...args);
    window.__solvitDefectModuleListDiag.lastReport = report;
    return report;
  };
}
